import React, { useState, useEffect } from 'react'
import { Modal, TextField } from '@mui/material'
import { useDispatch } from 'react-redux'
import { setMbData } from '../../../../js/lib/redux/slices/booking'
import SanitizeHtml from '../../Base/SanitizeHtml/SanitizeHtml'

const MilesNCashOTPModal = ({ open, handleClose, verifyOTP, requestOTP }) => {
  const { configuration, dictionary, token } =
    window.bookingData || window.modifyData
  const [code, setCode] = useState(new Array(6).fill(''))
  const [errorMsgs, setErrorMsgs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showTimer, setShowTimer] = useState(false)
  const [timer, setTimer] = useState(10)
  const [showRequestNewOTP, setShowRequestNewOTP] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (showTimer) {
      const timerInterval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer > 0) {
            return prevTimer - 1
          } else {
            setShowTimer(false)
            setShowRequestNewOTP(true)
            return 0
          }
        })
      }, 1000)
      return () => clearInterval(timerInterval)
    }
  }, [showTimer])

  useEffect(() => {
    if (!open) {
      setCode(new Array(6).fill(''))
      setErrorMsgs([])
      setIsLoading(false)
      setShowTimer(false)
      setShowRequestNewOTP(false)
      setTimer(10)
    }
  }, [open])

  const handleInputChange = (value, index) => {
    const newCode = [...code]
    newCode[index] = value.replace(/[^0-9]/g, '').slice(-1)
    setCode(newCode)

    // Automatically focus on the next input box if there is a valid value
    if (newCode[index] && index < 5) {
      document.getElementById(`code-input-${index + 1}`).focus()
    }
  }

  // Automatically focus on the previous input box with backspace
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-input-${index - 1}`).focus()
    }
  }

  const handleVerifyOTP = async () => {
    setIsLoading(true)
    try {
      const otpCode = code.join('')
      // Call the verifyOTP function passed as prop from BookingStep3
      const response = await verifyOTP(otpCode)

      const { HasError, Errors } = response

      if (HasError) {
        handleError(Errors)
        setShowRequestNewOTP(true)
        return
      }

      handleSuccess()
    } catch (err) {
      console.error(err)
      handleError([err.message])
    }
  }

  const handleResendOTP = async () => {
    setIsLoading(true)
    setShowRequestNewOTP(false)
    setCode(new Array(6).fill(''))
    setErrorMsgs([])
    try {
      // Note: requestOTP now expects the form data which is passed from BookingStep3
      // We don't need to pass selectedMiles anymore as it's taken from mbData in BookingStep3
      const response = await requestOTP()

      const { HasError, Errors } = response

      if (HasError) {
        handleError(Errors)
        return
      }

      setShowTimer(true)
      setTimer(10)
      handleSuccess()
    } catch (err) {
      console.error(err)
      handleError([err.message])
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (code.includes('')) return
    handleVerifyOTP()
  }

  const handleRequestNewOTP = async () => {
    if (showTimer) return
    handleResendOTP()
  }

  const handleSuccess = () => {
    setErrorMsgs([])
    setIsLoading(false)
  }

  const handleError = (messages) => {
    if (messages === null || messages === undefined) {
      setErrorMsgs([dictionary.defaultErrorMessage])
    } else if (typeof messages === 'string') {
      setErrorMsgs([messages])
    } else {
      setErrorMsgs(messages)
    }
    setIsLoading(false)
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="b-miles-n-cash-otp-modal__modal">
        <div className="b-miles-n-cash-otp-modal__image">
          <img
            src="/imgs/misc/otp-image.png"
            alt="otp-icon"
            width={64}
            height={48}></img>
        </div>
        <div className="b-miles-n-cash-otp-modal__title">
          {dictionary.milesNCashOTPLabel}
        </div>
        <SanitizeHtml
          className="b-miles-n-cash-otp-modal__message"
          html={
            'We have just sent a 6-digit password to the mobile phone number you <br>have on your Miles+Bonus account.'
          }
        />

        <form onSubmit={handleSubmit}>
          <div className="b-miles-n-cash-otp-modal__input-container">
            <div
              className={`
              b-miles-n-cash-otp-modal__input-boxes
              ${errorMsgs.length > 0 ? 'has-errors' : ''}
            `}>
              {code.map((digit, index) => (
                <TextField
                  key={index}
                  id={`code-input-${index}`}
                  className="b-miles-n-cash-otp-modal__input-box"
                  value={digit}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                  onChange={(e) => handleInputChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  disabled={isLoading}
                />
              ))}
            </div>
            {errorMsgs.length > 0 && (
              <div className="field-validation-error error">
                <i className="icon-warning" />
                &nbsp;
                {errorMsgs.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>

          <div className="b-miles-n-cash-otp-modal__buttons-container">
            {showRequestNewOTP && (
              <button
                type="button"
                className={`btn btn-link b-miles-n-cash-otp-modal__request-new-otp ${
                  showTimer ? 'disabled' : ''
                }`}
                onClick={handleRequestNewOTP}>
                {dictionary.milesNCashOTPSendNewCodeButtonLabel}{' '}
                {showTimer && `(${timer})`}
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary b-miles-n-cash-otp-modal__confirm-button"
              disabled={code.includes('') || isLoading}>
              {dictionary.milesNCashOTPConfrimButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default MilesNCashOTPModal
